/**
 * ChatResponseAppointment component for the Indigo design system.
 *
 * Compact row used to surface a single appointment inside a chat response.
 * Renders an icon tile on the left, a title / subtitle stack in the middle,
 * and an optional status chip / trailing node on the right.
 *
 * Predefined tones come with a baked-in palette:
 *   - `sky`   → sky-500 icon on sky-50 background (default).
 *   - `olive` → olive-600 icon on olive-50 background.
 *   - `amber` → amber-600 icon on amber-50 background.
 *
 * @example
 * ```tsx
 * <ChatResponseAppointment
 *   title="Follow-up with Dr. Annette Zhu"
 *   subtitle="Tue, Mar 12 · 9:00 AM"
 *   status={{ label: "Confirmed", variant: "done" }}
 *   onClick={() => openAppointment(id)}
 * />
 * ```
 */
import { clsx } from "clsx";
import { CSSProperties, forwardRef, ReactNode } from "react";
import { Icon } from "../../Icons";
import { StatusChip, StatusChipVariant } from "../../StatusChip/StatusChip";
import { Body } from "../../Typography/Body";
import "./ChatResponseAppointment.css";

export type ChatResponseAppointmentTone = "sky" | "olive" | "amber" | "custom";

export interface ChatResponseAppointmentStatus {
  /** Status text. */
  label: string;
  /** StatusChip variant (default `done`). */
  variant?: StatusChipVariant;
}

export interface ChatResponseAppointmentProps {
  /** Icon tone preset. Use `"custom"` to supply your own icon and colors. */
  tone?: ChatResponseAppointmentTone;
  /** Custom icon node (used when `tone === "custom"`, otherwise overrides default). */
  icon?: ReactNode;
  /** Foreground (icon) color when `tone === "custom"`. */
  iconColor?: string;
  /** Background color of the icon tile when `tone === "custom"`. */
  iconBgColor?: string;
  /** Title text. Rendered as Body S. */
  title: string;
  /** Subtitle text. Rendered as Body XS in tertiary color. */
  subtitle?: string;
  /** Optional status chip rendered on the right. */
  status?: ChatResponseAppointmentStatus;
  /** Optional trailing node — renders instead of `status` when provided. */
  trailing?: ReactNode;
  /** When provided, the row becomes interactive. */
  onClick?: () => void;
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

const TONE_ICON: Record<
  Exclude<ChatResponseAppointmentTone, "custom">,
  ReactNode
> = {
  sky: <Icon name="calendar-appointment" size="m" />,
  olive: <Icon name="calendar-appointment" size="m" />,
  amber: <Icon name="calendar-appointment" size="m" />,
};

export const ChatResponseAppointment = forwardRef<
  HTMLElement,
  ChatResponseAppointmentProps
>(
  (
    {
      tone = "sky",
      icon,
      iconColor,
      iconBgColor,
      title,
      subtitle,
      status,
      trailing,
      onClick,
      className,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    const isCustom = tone === "custom";
    const isInteractive = Boolean(onClick);
    const Component = isInteractive ? "button" : "div";

    const iconNode = isCustom ? icon : (icon ?? TONE_ICON[tone]);

    const customStyle: CSSProperties | undefined = isCustom
      ? {
          ...(iconBgColor ? { background: iconBgColor } : null),
          ...(iconColor ? { color: iconColor } : null),
        }
      : undefined;

    return (
      <Component
        ref={ref as React.Ref<HTMLButtonElement & HTMLDivElement>}
        type={isInteractive ? "button" : undefined}
        onClick={onClick}
        className={clsx(
          "indigo-chat-response-appointment",
          isInteractive && "indigo-chat-response-appointment--button",
          className,
        )}
        data-testid={dataTestId}
      >
        <span
          className={clsx(
            "indigo-chat-response-appointment__icon-wrap",
            !isCustom && `indigo-chat-response-appointment__icon-wrap--${tone}`,
          )}
          style={customStyle}
        >
          {iconNode}
        </span>
        <span className="indigo-chat-response-appointment__body">
          <Body
            as="span"
            size="sm"
            className="indigo-chat-response-appointment__title"
          >
            {title}
          </Body>
          {subtitle && (
            <Body
              as="span"
              size="xs"
              className="indigo-chat-response-appointment__subtitle"
            >
              {subtitle}
            </Body>
          )}
        </span>
        {(trailing || status) && (
          <span className="indigo-chat-response-appointment__trailing">
            {trailing ?? (
              <StatusChip
                label={status!.label}
                variant={status!.variant ?? "done"}
                size="small"
              />
            )}
          </span>
        )}
      </Component>
    );
  },
);

ChatResponseAppointment.displayName = "ChatResponseAppointment";

export default ChatResponseAppointment;
